import type { ChangeEvent, MouseEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'

import { useAccountPasswordChangeCreate } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { TextInput } from '@/components/text-input'
import { PasswordRulesChecker } from '@/pages/auth/components/password-rules-checker'
import { getAxiosErrorMessage } from '@/utils/helpers'

import { PasswordChangeSuccess } from './password-change-success'

type PasswordChangeFormData = {
  current_password: string
  new_password: string
  new_password2: string
}

export type ChangePasswordFormProps = {
  onClose: () => void
}

export const ChangePasswordForm = ({ onClose }: ChangePasswordFormProps) => {
  const { formatMessage } = useIntl()
  const [formValues, setFormValues] = useState<PasswordChangeFormData>({
    current_password: '',
    new_password: '',
    new_password2: '',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof PasswordChangeFormData, string>>>({})
  const [changeStatus, setChangeStatus] = useState<{
    status: 'warning' | 'success'
    message?: string
  } | null>(null)
  const { mutate: changePassword, isPending } = useAccountPasswordChangeCreate({
    mutation: {
      onSuccess: () => {
        setChangeStatus({
          status: 'success',
        })
      },
      onError: (error: AxiosError) => {
        setChangeStatus({
          status: 'warning',
          message:
            getAxiosErrorMessage(error) || formatMessage({ id: 'user.settings.newPassword.error' }),
        })
      },
    },
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear field error on change
    if (errors[name as keyof PasswordChangeFormData] !== undefined) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PasswordChangeFormData, string>> = {}

    if (!formValues.current_password) {
      newErrors.current_password = formatMessage({ id: 'common.validation.required' })
    }

    if (!formValues.new_password) {
      newErrors.new_password = formatMessage({ id: 'common.validation.required' })
    }

    if (!formValues.new_password2) {
      newErrors.new_password2 = formatMessage({ id: 'common.validation.required' })
    }

    if (
      formValues.new_password &&
      formValues.new_password2 &&
      formValues.new_password !== formValues.new_password2
    ) {
      newErrors.new_password2 = formatMessage({ id: 'common.validation.passwordsDoNotMatch' })
    }

    setErrors(newErrors)

    return Object.keys(newErrors).length === 0
  }

  const onSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    if (validate()) {
      changePassword({
        data: {
          current_password: formValues.current_password,
          new_password: formValues.new_password,
          new_password2: formValues.new_password2,
        },
      })
    }
  }

  // Show success screen
  if (changeStatus?.status === 'success') {
    return <PasswordChangeSuccess onClose={onClose} />
  }

  return (
    <div className="w-full">
      <FormContainer>
        <div className="flex flex-col gap-6">
          <TextInput
            error={errors.current_password}
            label="user.settings.currentPassword"
            input={{
              disabled: isPending,
              name: 'current_password',
              type: 'password',
              value: formValues.current_password,
              onChange,
            }}
          />

          <TextInput
            error={errors.new_password}
            label="user.settings.newPassword"
            input={{
              disabled: isPending,
              name: 'new_password',
              type: 'password',
              value: formValues.new_password,
              onChange,
            }}
          />

          <TextInput
            error={errors.new_password2}
            label="user.settings.newPassword.confirmation"
            input={{
              disabled: isPending,
              name: 'new_password2',
              type: 'password',
              value: formValues.new_password2,
              onChange,
            }}
          />

          <PasswordRulesChecker password={formValues.new_password} />
        </div>

        {changeStatus?.status === 'warning' && (
          <Alert
            heading={changeStatus.message}
            severity="warning"
            onClose={() => setChangeStatus(null)}
          />
        )}

        <div className="flex gap-4">
          <Button
            color="secondary"
            disabled={isPending}
            size="large"
            variant="contained"
            onClick={onClose}
          >
            <FormattedMessage id="common.controls.cancel" />
          </Button>

          <Button
            className="flex-[1_0_auto]"
            color="primary"
            disabled={isPending}
            loading={isPending}
            size="large"
            type="submit"
            variant="contained"
            onClick={onSubmit}
          >
            <FormattedMessage id="common.controls.changePassword" />
          </Button>
        </div>
      </FormContainer>
    </div>
  )
}
